/** Service Desk console: Assets and the asset detail (C06/C07, SD-S3-10). */
export const serviceAssets = {
  subtitle: "The installed base across your customers: serials, warranty cover, contracts and maintenance.",
  relative: { today: "today", in_one: "in {{count}} day", in_other: "in {{count}} days", ago_one: "{{count}} day ago", ago_other: "{{count}} days ago" },
  orgTab: { viewAll: "View all assets", emptyTitle: "No equipment yet", emptyBody: "Assets appear here once the client accepts the work, when you add one, or after an import." },
  sections: { label: "Assets sections", assets: "Assets", contracts: "Contracts" },
  actions: {
    create: "New asset",
    import: "Import CSV",
    labels: "Print QR labels",
    labelOne: "Print QR label",
    edit: "Edit asset",
    move: "Move asset",
    archive: "Archive asset",
    restore: "Restore",
  },
  stats: {
    label: "Summary",
    assets: "Assets",
    assetsHint: "not archived",
    covered: "Under warranty",
    coveredHint: "including expiring soon",
    expiring: "Expiring soon",
    expiringHint: "inside the warranty window",
    overdue: "Maintenance overdue",
    overdueHint: "needs a visit",
  },
  overdue: {
    banner_one: "{{count}} asset has maintenance overdue:",
    banner_other: "{{count}} assets have maintenance overdue:",
    more_one: " and {{count}} more.",
    more_other: " and {{count}} more.",
  },
  filters: {
    warrantyGroup: "Warranty filter",
    warranty: { all: "All", in_warranty: "In warranty", expiring: "Expiring soon", expired: "Expired", unknown: "Not started or unknown" },
    search: "Search tag, serial or product",
    searchLabel: "Search assets",
    organisation: "Organisation",
    allOrganisations: "All organisations",
    status: "Status",
    allStatuses: "All statuses",
    category: "Category",
    categoryLabel: "Filter by category",
    allCategories: "All categories",
    clear: "Clear filters",
  },
  status: { active: "Active", under_repair: "Under repair", decommissioned: "Decommissioned", returned: "Returned" },
  due: { pm: "PM", calibration: "Calibration", overdueBy_one: "overdue by {{count}} day", overdueBy_other: "overdue by {{count}} days" },
  list: {
    label: "Assets",
    asset: "Asset",
    organisation: "Organisation",
    warranty: "Warranty",
    status: "Status",
    nextDue: "Next due",
    warrantyEnds: "Ends {{date}}",
    selectAll: "Select all assets",
    select: "Select {{tag}}",
    showing: "Showing {{shown}} of {{total}} assets",
    loadMore: "Load more",
  },
  bulk: { label: "Bulk actions", selected_one: "{{count}} selected", selected_other: "{{count}} selected", clear: "Clear selection" },
  empty: {
    title: "No assets yet",
    body: "Register the equipment you installed or sold, with its serial number and warranty, so tickets and visits know what they are about.",
    filteredTitle: "No assets match",
    filteredBody: "Try another tag or serial, or clear a filter.",
  },
  error: { title: "Assets couldn't load", body: "Check your connection and try again. Nothing was changed." },
  errors: {
    serialTaken: "Another asset already has this serial number.",
    archived: "This asset is archived. Restore it before changing it.",
    siteInUse: "This site still has assets.",
  },
  form: {
    createTitle: "New asset",
    createDescription: "A tag such as AST-0001 is given when you save.",
    editTitle: "Edit asset",
    editDescription: "Changes to {{tag}} are kept in its timeline.",
    organisation: "Organisation",
    chooseOrganisation: "Choose an organisation",
    site: "Site",
    noSite: "No site",
    noSites: "This organisation has no sites yet.",
    name: "Product name",
    namePlaceholder: "Name of the product or equipment",
    category: "Category",
    categoryNone: "No category",
    manufacturer: "Manufacturer",
    model: "Model",
    serial: "Serial number",
    serialHint: "A serial number is unique for each manufacturer.",
    nickname: "Nickname",
    location: "Location on site",
    purchasedOn: "Purchased on",
    installedOn: "Installed on",
    acceptedOn: "Accepted on",
    status: "Status",
    warranty: "Warranty",
    warrantyHint: "Optional. A warranty with a length needs a start date.",
    warrantyStart: "Starts on",
    warrantyMonths: "Length (months)",
    warrantyBasis: "Starts at",
    warrantyProvider: "Provided by",
    notes: "Notes",
    cancel: "Cancel",
    create: "Create asset",
    save: "Save changes",
    created: "{{tag}} created",
    updated: "Asset updated",
  },
  validation: {
    organisation: "Choose the organisation this asset belongs to.",
    name: "Give the asset a name of up to {{max}} characters.",
    serial: "A serial number has at most {{max}} characters.",
    text: "At most {{max}} characters.",
    months: "Enter a whole number of months from 1 to {{max}}.",
    warrantyStart: "Set the date the warranty starts.",
  },
  labels: {
    title: "Print QR labels",
    description_one: "{{count}} label on {{sheets}} A4 sheet. Each label opens the asset's page when scanned.",
    description_other: "{{count}} labels on {{sheets}} A4 sheet(s). Each label opens the asset's page when scanned.",
    startAt: "Start at label",
    startHint: "Use 1 for a fresh sheet; a higher number skips labels already used (1 to {{max}}).",
    startInvalid: "Enter a number from 1 to {{max}}.",
    previewLabel: "Sheet preview: printing starts at label {{start}}, {{count}} on this sheet",
    capped: "Only the first {{max}} assets fit one request.",
    download: "Download PDF",
    done_one: "{{labels}} label on {{sheets}} sheet saved as PDF",
    done_other: "{{labels}} labels on {{sheets}} sheet(s) saved as PDF",
  },
  upload: {
    blocked: "This file was stopped by the security scan.",
    openFailed: "That file could not be opened. Try again.",
    refused: {
      count: "Too many files.",
      type: "This type of file is not accepted. Use a PDF, image or text file.",
      size: "This file is too large.",
      empty: "This file is empty.",
    },
  },
  detail: {
    back: "Assets",
    loading: "Loading the asset",
    sections: "Asset sections",
    more: "More actions",
    notFoundTitle: "Asset not found",
    notFoundBody: "It may have been archived or moved. Go back to the list to find it.",
    archivedBanner: "This asset is archived and read-only. Restore it to change it.",
    pmOverdue: "Maintenance overdue",
    serialNo: "SN {{serial}}",
    archiveTitle: "Archive {{tag}}?",
    archiveBody: "It leaves the asset list and cannot be changed until you restore it. Its history is kept.",
    archiveConfirm: "Archive asset",
    archived: "{{tag}} archived",
    restored: "{{tag}} restored",
  },
  tabs: { overview: "Overview", warranty: "Warranty & contracts", tickets: "Tickets", rmas: "Returns", parts: "Parts", documents: "Documents", events: "Events" },
  overview: {
    details: "Details",
    serial: "Serial",
    model: "Model",
    category: "Category",
    nickname: "Nickname",
    site: "Site",
    location: "Location",
    purchased: "Purchased",
    installed: "Installed",
    accepted: "Accepted",
    awaitingAcceptance: "Awaiting acceptance",
    contract: "Contract",
    noContract: "No contract covers this asset",
    notes: "Notes",
    maintenance: "Maintenance plan",
    label: "Asset label",
    qrLabel: "QR code for {{tag}}",
    openTickets: "Open tickets",
    noOpenTickets: "No open tickets",
  },
  maintenance: {
    label: "Maintenance plan",
    task: "Task",
    lastDone: "Last done",
    nextDue: "Next due",
    actions: "Actions",
    markDone: "Mark done",
    kind: { pm: "Preventive maintenance", calibration: "Calibration" },
    everyMonths_one: "every {{count}} month",
    everyMonths_other: "every {{count}} months",
    everyUnits: "every {{count}} {{unit}}",
    atMeter: "at {{value}} {{unit}}",
    emptyTitle: "No maintenance plan",
    emptyBody: "Add a preventive maintenance or calibration plan to get reminders before it is due.",
    errorTitle: "The maintenance plan couldn't load",
    errorBody: "Check your connection and try again.",
    doneTitle: "Mark {{task}} done?",
    doneBody: "The service on {{tag}} counts as done today, and the next due date starts from today.",
    doneConfirm: "Mark done",
    doneSaved: "Marked as done",
  },
  warranty: {
    title: "Warranty",
    extend: "Extend warranty",
    status: { in_warranty: "In warranty", expiring: "Expiring soon", expired: "Expired", unknown: "Unknown" },
    chip: {
      inWarranty: "In warranty until {{date}}",
      expiring_one: "Expires in {{count}} day",
      expiring_other: "Expires in {{count}} days",
      expiringToday: "Expires today",
      expired: "Expired {{date}}",
      notStarted: "Starts {{date}}",
      unknown: "No warranty on file",
    },
    basis: { acceptance: "Acceptance", installation: "Installation", delivery: "Delivery", manual: "Set by hand" },
    cover: { parts: "Parts", labor: "Labour", travel: "Travel" },
    covered: "Covered",
    notCovered: "Not covered",
    expiredNote: "The warranty has expired. A repair on this asset is chargeable unless a contract covers it.",
    elapsedLabel: "Warranty period used",
    elapsed: "{{percent}} percent of the warranty period has passed",
    period: "From {{start}} to {{end}}",
    noDates: "No warranty dates are set. Use Extend warranty to add them.",
    starts: "Starts",
    atAcceptance: "At acceptance",
    ends: "Ends",
    provider: "Provided by",
    covers: "Covers",
    terms: "Terms source",
    termsLinked: "A knowledge source holds the terms",
    termsNone: "No terms source linked. The assistant says the team will confirm what is covered.",
    coverageTitle: "Coverage",
    item: "Item",
    contract: "Contract",
    coverageNote: "What the assistant tells a client about cover always comes from the terms source, never from a guess.",
    contractTitle: "Contract and SLA",
    openContract: "Open contract",
    contractError: "The contract could not be loaded.",
    noContractTitle: "No contract",
    noContractBody: "Tickets for this asset use the workspace default SLA.",
    sla: "SLA {{name}}",
    defaultSla: "Workspace default SLA",
  },
  warrantyDialog: {
    title: "Warranty",
    description: "Replaces the whole warranty record of {{tag}}. The reason goes on its timeline.",
    startOn: "Starts on",
    endOn: "Ends on",
    months: "or length in months",
    monthsHint: "Counted from the start date. Leave empty when you give an end date.",
    basis: "Starts at",
    provider: "Provided by",
    covers: "Covers",
    reason: "Reason",
    reasonHint: "Optional. For example goodwill after a repeated failure.",
    save: "Save warranty",
    saved: "Warranty saved",
    problems: {
      end: "Give an end date or a length in months.",
      bothEndAndMonths: "Give an end date or a length in months, not both.",
      order: "The warranty cannot end before it starts.",
      months: "Enter a whole number of months from 1 to 240.",
      monthsNeedStart: "A length in months needs a start date.",
    },
  },
  moveDialog: {
    title: "Move asset",
    description: "Choose where {{tag}} is now. This is audited.",
    organisation: "Organisation",
    site: "Site",
    confirm: "I confirm that moving the asset to another organisation changes who can see it.",
    reason: "Reason",
    save: "Move asset",
    moved: "{{tag}} moved",
    problems: { nothing: "Choose another site or organisation.", confirm: "Tick the box to confirm the move to another organisation." },
  },
  tickets: {
    title: "Tickets for this asset",
    ticket: "Ticket",
    titleCol: "Title",
    status: "Status",
    created: "Created",
    emptyTitle: "No tickets for this asset",
    emptyBody: "Tickets raised against this asset appear here.",
    errorTitle: "Tickets couldn't load",
    errorBody: "Check your connection and try again.",
  },
  parts: {
    title: "Parts replaced",
    record: "Record part",
    date: "Date",
    part: "Part",
    coverageCol: "Covered by",
    coverage: { warranty: "Warranty", contract: "Contract", chargeable: "Chargeable" },
    removed: "Removed serial",
    new: "New serial",
    emptyTitle: "No parts replaced yet",
    emptyBody: "Parts recorded on a visit or here show up with their serial numbers.",
    errorTitle: "Parts couldn't load",
    errorBody: "Check your connection and try again.",
  },
  partDialog: {
    title: "Record a part",
    description: "It also lands on the asset's timeline.",
    partDescription: "Part",
    quantity: "Quantity",
    sku: "SKU",
    coverage: "Covered by",
    removedSerial: "Serial removed",
    newSerial: "Serial fitted",
    save: "Record part",
    saved: "Part recorded",
    problems: { description: "Describe the part in up to {{max}} characters.", quantity: "Enter a whole number from 1 to {{max}}." },
  },
  documents: {
    title: "Documents",
    add: "Add document",
    addTitle: "Add a document",
    addDescription: "Files are checked and scanned before anyone can open them.",
    file: "File",
    fileHint: "PDF, image or text file.",
    titleLabel: "Title",
    kind: "Type",
    kinds: { manual: "Manual", warranty_card: "Warranty card", certificate: "Certificate", report: "Report", photo: "Photo", other: "Other" },
    validUntil: "Valid until",
    clientVisible: "Visible to the client in the portal",
    clientVisibleHint: "Leave off to keep it for staff only.",
    upload: "Upload",
    added: "Document added",
    document: "Document",
    client: "Client portal",
    visible: "Visible",
    staffOnly: "Staff only",
    visibleLabel: "Visible to the client: {{title}}",
    nowVisible: "The client can now see this document",
    nowStaffOnly: "This document is now for staff only",
    actions: "Actions",
    open: "Open {{title}}",
    remove: "Remove {{title}}",
    removeTitle: "Remove {{title}}?",
    removeBody: "The document leaves the asset. The file is deleted.",
    removeConfirm: "Remove document",
    removed: "Document removed",
    validTo: "valid until {{date}}",
    emptyTitle: "No documents yet",
    emptyBody: "Add a manual, warranty card or certificate and choose whether the client may see it.",
    errorTitle: "Documents couldn't load",
    errorBody: "Check your connection and try again.",
    problems: { file: "Choose a file to upload.", title: "Give the document a title." },
  },
  events: {
    title: "Events",
    filter: "Event filter",
    groups: { all: "All", tickets: "Tickets", visits: "Visits", parts: "Parts", warranty: "Warranty", other: "Other" },
    kind: {
      installed: "Installed",
      accepted: "Accepted by the client",
      ticket_opened: "Ticket opened",
      ticket_resolved: "Ticket resolved",
      visit: "Visit",
      part_replaced: "Part replaced",
      calibration_done: "Calibration done",
      pm_done: "Maintenance done",
      moved_site: "Moved to another site",
      warranty_extended: "Warranty extended",
      status_changed: "Status changed",
      created: "Asset registered",
      updated: "Details updated",
      moved_organisation: "Moved to another organisation",
      archived: "Archived",
      restored: "Restored",
      document_added: "Document added",
    },
    actor: { staff: "Staff", contact: "Client", system: "System", connector: "Connector", import: "Import" },
    emptyTitle: "No events yet",
    emptyBody: "What happens to this asset is recorded here.",
    emptyFilteredTitle: "No events for this filter",
    emptyFilteredBody: "Choose All to see the whole timeline.",
    errorTitle: "Events couldn't load",
    errorBody: "Check your connection and try again.",
  },
} as const;
